// =============================================================================
// iTravel 2.0 — API Client (ky)
// =============================================================================
// Configured ky instance with authentication interceptors, token refresh,
// offline detection, and error handling.
// =============================================================================

import ky, {
  type AfterResponseHook,
  type BeforeRequestHook,
  type BeforeRetryHook,
  type KyInstance,
  type Options,
} from 'ky';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface TokenStore {
  getAccessToken: () => string | null;
  getRefreshToken: () => string | null;
  setTokens: (accessToken: string, refreshToken: string) => void;
  clearTokens: () => void;
}

interface ApiClientConfig {
  baseUrl: string;
  tokenStore: TokenStore;
  onUnauthorized?: () => void;
  onNetworkError?: () => void;
  /** Any response at all: the server is reachable, so the network is up. */
  onServerReachable?: () => void;
}

// ---------------------------------------------------------------------------
// Token refresh state (shared across all requests to prevent parallel refreshes)
// ---------------------------------------------------------------------------

let isRefreshing = false;
let refreshPromise: Promise<boolean> | null = null;

// ---------------------------------------------------------------------------
// Refresh token logic
// ---------------------------------------------------------------------------

async function refreshAccessToken(baseUrl: string, tokenStore: TokenStore): Promise<boolean> {
  const refreshToken = tokenStore.getRefreshToken();
  if (!refreshToken) {
    return false;
  }

  try {
    const response = await fetch(`${baseUrl}/api/v1/auth/refresh`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refreshToken }),
    });

    if (!response.ok) {
      tokenStore.clearTokens();
      return false;
    }

    const data = (await response.json()) as {
      data: { accessToken: string; refreshToken: string };
    };

    tokenStore.setTokens(data.data.accessToken, data.data.refreshToken);
    return true;
  } catch {
    tokenStore.clearTokens();
    return false;
  }
}

/**
 * Attempt to refresh the access token. Multiple concurrent calls will share
 * the same in-flight refresh request to avoid race conditions.
 */
async function refreshTokenIfNeeded(baseUrl: string, tokenStore: TokenStore): Promise<boolean> {
  if (isRefreshing && refreshPromise) {
    return refreshPromise;
  }

  isRefreshing = true;
  refreshPromise = refreshAccessToken(baseUrl, tokenStore).finally(() => {
    isRefreshing = false;
    refreshPromise = null;
  });

  return refreshPromise;
}

// ---------------------------------------------------------------------------
// Hook factories
// ---------------------------------------------------------------------------

function createBeforeRequestHook(tokenStore: TokenStore): BeforeRequestHook {
  return (request) => {
    const accessToken = tokenStore.getAccessToken();
    if (accessToken) {
      request.headers.set('Authorization', `Bearer ${accessToken}`);
    }
  };
}

function createBeforeRetryHook(baseUrl: string, tokenStore: TokenStore): BeforeRetryHook {
  return async ({ request, error }) => {
    // Only attempt token refresh on 401 responses
    if (!('response' in error) || (error as { response?: Response }).response?.status !== 401) {
      return;
    }

    const refreshed = await refreshTokenIfNeeded(baseUrl, tokenStore);
    if (refreshed) {
      const newToken = tokenStore.getAccessToken();
      if (newToken) {
        request.headers.set('Authorization', `Bearer ${newToken}`);
      }
    }
  };
}

function createAfterResponseHook(
  baseUrl: string,
  tokenStore: TokenStore,
  onUnauthorized?: () => void,
): AfterResponseHook {
  return async (request, _options, response) => {
    // If the response is 401, try refreshing the token and retry the request
    if (response.status === 401) {
      // Skip refresh logic for auth endpoints to avoid infinite loops
      const url = new URL(request.url);
      if (
        url.pathname.includes('/auth/refresh') ||
        url.pathname.includes('/auth/login') ||
        url.pathname.includes('/auth/register')
      ) {
        onUnauthorized?.();
        return response;
      }

      const refreshed = await refreshTokenIfNeeded(baseUrl, tokenStore);
      if (refreshed) {
        // Clone the original request with the new token
        const newToken = tokenStore.getAccessToken();
        if (newToken) {
          const retryRequest = new Request(request, {
            headers: new Headers(request.headers),
          });
          retryRequest.headers.set('Authorization', `Bearer ${newToken}`);

          return ky(retryRequest);
        }
      }

      // Refresh failed — user needs to re-authenticate
      onUnauthorized?.();
    }

    return response;
  };
}

// ---------------------------------------------------------------------------
// Create API Client
// ---------------------------------------------------------------------------

export function createApiClient(config: ApiClientConfig): KyInstance {
  const { baseUrl, tokenStore, onUnauthorized, onNetworkError, onServerReachable } = config;

  const instance = ky.create({
    prefixUrl: `${baseUrl}/api/v1`,
    timeout: 30_000,
    retry: {
      limit: 2,
      methods: ['get', 'put', 'patch', 'delete'],
      statusCodes: [408, 429, 500, 502, 503, 504],
      backoffLimit: 3000,
    },
    hooks: {
      beforeRequest: [createBeforeRequestHook(tokenStore)],
      beforeRetry: [createBeforeRetryHook(baseUrl, tokenStore)],
      afterResponse: [
        (_request, _options, response) => {
          onServerReachable?.();
          return response;
        },
        createAfterResponseHook(baseUrl, tokenStore, onUnauthorized),
      ],
      beforeError: [
        (error) => {
          // Enrich the error with a friendlier message for network failures
          if (error.name === 'TypeError' && error.message === 'Failed to fetch') {
            onNetworkError?.();
          }
          return error;
        },
      ],
    },
    headers: {
      Accept: 'application/json',
    },
  });

  return instance;
}

// ---------------------------------------------------------------------------
// Typed request helpers
// ---------------------------------------------------------------------------

export interface PaginatedResponse<T> {
  data: T[];
  meta: {
    page: number;
    perPage: number;
    total: number;
    totalPages: number;
  };
}

export interface SingleResponse<T> {
  data: T;
}

export interface CursorPaginatedResponse<T> {
  data: T[];
  meta: {
    nextCursor: string | null;
    hasMore: boolean;
    totalCount?: number;
  };
}

/**
 * Helper to build query string params from an object, omitting undefined/null values.
 */
export function buildSearchParams(
  params: Record<string, string | number | boolean | undefined | null>,
): URLSearchParams {
  const searchParams = new URLSearchParams();

  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined && value !== null && value !== '') {
      searchParams.set(key, String(value));
    }
  }

  return searchParams;
}

// ---------------------------------------------------------------------------
// Default singleton instance
// ---------------------------------------------------------------------------
// The actual instance is created and exported from the app initialization layer
// (see @/app/providers.tsx) once the auth store is available.
// This file exports the factory so it can be created with the right dependencies.
// ---------------------------------------------------------------------------

export type { ApiClientConfig, Options as KyOptions, TokenStore };
